import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowUpRight, CheckCircle2, RefreshCw } from 'lucide-react';
import { supabaseService } from '../supabaseService';
import AdsterraBanner from './AdsterraBanner';
import AdsterraNativeBanner from './AdsterraNativeBanner';

export interface AdUnitProps {
  slotId?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal';
  className?: string;
  minHeight?: number | string;
  fallbackOnly?: boolean;
}

interface SponsorAd {
  id: string;
  brand: string;
  heading: string;
  description: string;
  url: string;
  buttonText: string;
  earningsText: string;
  gradient: string;
  ratingText: string;
}

const SPONSOR_ADS: SponsorAd[] = [
  {
    id: 'masterclass',
    brand: 'MasterClass Creative',
    heading: 'Margaret Atwood Teaches Creative Writing & Fiction',
    description: 'Learn dynamic character creation, worldbuilding, and how to craft rich literary themes from a true master.',
    url: 'https://masterclass.com',
    buttonText: 'Claim Writer Discount',
    earningsText: 'Authored Click Share: +$0.03 platform ad yield credited.',
    gradient: 'from-amber-600/90 via-amber-900 to-black',
    ratingText: '4.9 ★ (18.4K Writers Certified)'
  },
  {
    id: 'scrivener',
    brand: 'Scrivener Studio',
    heading: 'Your Complete Manuscript Workspace is Awaiting',
    description: 'Structure chapters, store research, build outlines, and draft your masterpiece in a tailored author editor.',
    url: 'https://literatureandlatte.com/scrivener',
    buttonText: 'Download 30-Day Free Trial',
    earningsText: 'Authored Click Share: +$0.04 publisher ad yield credited.',
    gradient: 'from-emerald-700/90 via-emerald-950 to-emerald-950',
    ratingText: '4.8 ★ (Industry Standard)'
  }
];

const AdUnit: React.FC<AdUnitProps> = ({
  format = 'auto',
  className = '',
  minHeight = 120,
  fallbackOnly = false
}) => {
  const [isAdFreeUser, setIsAdFreeUser] = useState(false);
  const [sponsor, setSponsor] = useState<SponsorAd>(SPONSOR_ADS[0]);
  const [isClaimed, setIsClaimed] = useState(false);

  useEffect(() => {
    supabaseService.getCurrentUser().then(u => {
      if (u && ((u as any).is_supporter || (u as any).role === 'admin_tester_ad_free')) {
        setIsAdFreeUser(true);
      }
    }).catch(() => {});
  }, []);

  // Randomize fallback sponsor on mount
  useEffect(() => {
    const randomAd = SPONSOR_ADS[Math.floor(Math.random() * SPONSOR_ADS.length)];
    setSponsor(randomAd);
  }, []);

  // Suppress ads for supporters or users in zero-ad tiers if requested
  if (isAdFreeUser) {
    return null;
  }

  const handleSponsorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsClaimed(true);
    const existing = parseFloat(localStorage.getItem('literally_mock_ad_earnings') || '0.00');
    const newTotal = parseFloat((existing + 0.03).toFixed(4));
    localStorage.setItem('literally_mock_ad_earnings', newTotal.toFixed(4));
  };

  const handleSponsorNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClaimed(false);
    const remaining = SPONSOR_ADS.filter(x => x.id !== sponsor.id);
    const nextAd = remaining[Math.floor(Math.random() * remaining.length)] || SPONSOR_ADS[0];
    setSponsor(nextAd);
  };

  const minHeightStyle = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;

  return (
    <div
      className={`ad-container relative w-full overflow-hidden select-none transition-all duration-300 ${className}`}
      style={{ minHeight: minHeightStyle }}
    >
      {/* Active Adsterra Monetized Banners */}
      {!fallbackOnly && (
        <div className="w-full flex justify-center items-center py-2">
          {format === 'horizontal' ? (
            <AdsterraNativeBanner />
          ) : (
            <AdsterraBanner />
          )}
        </div>
      )}

      {/* Community Sponsor Card (Explicit fallback only) */}
      {fallbackOnly && (
        <div className="relative w-full">
          <div className="absolute top-3 left-4 z-10 flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-full text-[8px] font-black uppercase tracking-[0.2em] text-amber-400">
            <Sparkles size={8} />
            <span>COMMUNITY SPONSOR</span>
          </div>

          <div className="absolute top-3 right-4 z-10 text-[8px] font-bold text-slate-400/90 bg-black/20 backdrop-blur-md px-2 py-1 rounded-lg">
            {sponsor.ratingText}
          </div>

          {isClaimed ? (
            <div className="bg-gradient-to-br from-indigo-950 to-slate-950 border-2 border-indigo-400/40 rounded-3xl p-6 flex flex-col items-center justify-center text-center text-white relative animate-fade-in">
              <div className="p-3 bg-emerald-500/20 text-emerald-400 rounded-full mb-2 animate-bounce">
                <CheckCircle2 size={28} />
              </div>
              
              <h4 className="text-sm font-black uppercase tracking-wider text-emerald-400">Support recorded</h4>
              <p className="text-xs text-slate-300 max-w-[340px] mt-1 font-medium leading-relaxed">
                {sponsor.earningsText}
              </p>

              <button 
                onClick={handleSponsorNext}
                className="mt-4 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-white flex items-center gap-1.5 bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-full transition-all"
              >
                <RefreshCw size={10} />
                <span>Next Sponsor</span>
              </button>
            </div>
          ) : (
            <div
              onClick={handleSponsorClick}
              className={`cursor-pointer block bg-gradient-to-br ${sponsor.gradient} border border-slate-200/50 dark:border-slate-800 rounded-3xl p-5 sm:p-6 relative overflow-hidden transition-all duration-300 shadow-sm active:scale-[0.99] group`}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

              <div className="relative z-10 mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <span className="text-[10px] font-black tracking-widest text-indigo-300 uppercase leading-none block">
                    {sponsor.brand}
                  </span>
                  <h3 className="text-sm sm:text-base font-black text-white hover:text-indigo-200 transition-colors leading-tight tracking-tight serif">
                    {sponsor.heading}
                  </h3>
                  <p className="text-xs text-slate-300 font-medium leading-relaxed line-clamp-2 max-w-[480px]">
                    {sponsor.description}
                  </p>
                </div>

                <div className="flex shrink-0 items-center justify-center">
                  <span className="px-4 py-2.5 bg-white hover:bg-indigo-50 text-indigo-950 rounded-2xl text-xs font-black tracking-tight flex items-center justify-center gap-1.5 shadow-lg transition-transform hover:scale-[1.02] active:scale-95">
                    <span>{sponsor.buttonText}</span>
                    <ArrowUpRight size={13} className="text-indigo-600" />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdUnit;
